import {
  Button,
  Divider,
  FormModifier,
  Headline,
  IconLogoMark,
  Link,
  Subhead,
  Text,
} from '@hudl/uniform-ui-components';
import React, { useCallback, useState } from 'react';
import LoginInput from '../LoginInput';
import * as styles from './styles.scss';
import g11n from 'hudl-g11n';
import ErrorDisplay from '../../shared/ErrorDisplay';
import { StartPasswordResetResponse } from 'requests/rest/startPasswordReset';
const { FormattedHTMLMessage } = g11n.ReactIntl;

interface LoginHelpProps {
  email: string;
  setEmail: (email: string) => void;
  onPasswordReset: (email: string) => Promise<StartPasswordResetResponse>;
}

const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const LoginHelp = (props: LoginHelpProps) => {
  const { email, onPasswordReset, setEmail } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onPasswordResetClick = useCallback(
    async e => {
      e?.preventDefault();
      setIsLoading(true);

      const response = await onPasswordReset(email);

      setIsLoading(false);

      if (!response.success) {
        if (!emailRegex.test(email)) {
          setErrorMessage(g11n.format('login_invalid_email_format'));
        } else {
          setErrorMessage(g11n.format('login_unknown_email_address'));
        }
      }
    },
    [email, onPasswordReset],
  );

  const onSetEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);

      if (errorMessage !== null) {
        setErrorMessage(null);
      }
    },
    [errorMessage, setEmail],
  );

  return (
    <form className={styles.formContainer}>
      <Link href="/" type="wrapper" className={styles.hudlLogoContainer}>
        <IconLogoMark qaId="hudl-logo" size="large" />
      </Link>

      <FormModifier className={styles.formModifierContainer} formSize="large" density="default">
        <Headline level="2" qaId="login-help-headline">
          {g11n.format('login_login_help')}
        </Headline>
        <Headline level="3" qaId="lets-reset-password-headline" className="uni-margin--one--top">
          {g11n.format('login_password_reset')}
        </Headline>
        <LoginInput
          type="text"
          label={g11n.format('login_email')}
          className="uni-margin--one-and-half--top"
          onChange={onSetEmail}
          value={email}
          qaId="password-reset-input"
        />

        <ErrorDisplay qaId="password-reset-error-display">{errorMessage}</ErrorDisplay>

        <Button
          type="submit"
          size="large"
          className="uni-margin--one--top"
          onClick={onPasswordResetClick}
          isDisabled={!email?.length || isLoading}
          status={isLoading ? 'spinning' : undefined}
          qaId="password-reset-submit-btn">
          {g11n.format('login_send_password_reset')}
        </Button>

        <Divider className="uni-margin--one-and-half--top uni-margin--one-and-half--bottom" />

        <Headline level="3" qaId="email-help-headline">
          {g11n.format('login_email_help')}
        </Headline>

        <Subhead className="uni-margin--one--top" qaId="forgot-email-headline">
          {g11n.format('login_link_forgot_email')}
        </Subhead>
        <Text className="uni-margin--half--top" qaId="forgot-email-copy">
          <FormattedHTMLMessage id="login_forgot_email_copy" />
        </Text>

        <Subhead className="uni-margin--one--top" qaId="didnt-receive-headline">
          {g11n.format('login_forgot_email_didnt_receive_header')}
        </Subhead>
        <Text className="uni-margin--half--top" qaId="didnt-receive-copy">
          <FormattedHTMLMessage id="login_forgot_email_didnt_receive_copy" />
        </Text>

        <Subhead className="uni-margin--one--top" qaId="still-trouble-headline">
          {g11n.format('login_forgot_password_still_trouble_header')}
        </Subhead>
        <Text className="uni-margin--half--top" qaId="still-trouble-copy">
          <FormattedHTMLMessage id="login_forgot_email_contact_support" />
        </Text>
      </FormModifier>
    </form>
  );
};

export default LoginHelp;
